import { useEffect, useRef } from 'react';
import { NO_ELEMENT, withCurrentPage, rm } from 'utils';
import { ProductList } from './productList';
import { ProductlistPaging } from '../BGH/productlistPaging';
import { Header } from './header';
import { Filter } from 'components/Filter/themes/PP/filter';
import { BrandContentBottom } from '../BGH/brandContent';
import { Section } from 'components/Section';
import { CategoryNavigation, FilteredCategoryNavigation } from '../BGH/categoryNavigation';
import styles from './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  getHasCategories,
  getNumberOfFaqs,
  getHeader,
  showFilter,
  getBrandContentDisplayType,
  hasBrandContent,
  selectWebCatalog,
  getBrandContent
} from 'views/ProductlistPage/selectors';
import { CategoryFaqs } from 'views/ProductPage/faqs';
import { getFaqs } from 'views/ProductPage/faqs/themes/BGH/selector';
import { isMobileLayout } from 'components/Shared/responsive';
import { BrandContentDisplayType } from 'models/BrandContentDisplayType';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { GtmSelectItem, GtmViewListItem } from 'store/googleTagManager/actions';
import {
  getBrandListName,
  getProductListName,
  mapProductListViewProductToGtm
} from 'store/googleTagManager/events/utils';
import { GtmItemProduct } from 'store/googleTagManager/types';
import { GtmViewItem } from 'views/ProductlistPage/themes/BGH/productList';

const useBrandContentScroll = (displayType: BrandContentDisplayType) => {
  const ref = useRef<HTMLDivElement>(NO_ELEMENT);
  useEffect(() => {
    const shouldScroll = displayType === BrandContentDisplayType.ScrollPast;
    let handle: number;
    if (shouldScroll) {
      handle = window.setTimeout(() => {
        ref.current && ref.current.scrollIntoView();
      }, 0);
    }
    return () => {
      handle && window.clearTimeout(handle);
    };
  }, [displayType]);

  return ref;
};

export const ProductlistPage = () => {
  const brandContentDisplayType = useSelector(withCurrentPage(getBrandContentDisplayType));
  const footerRef = useRef<HTMLDivElement>(NO_ELEMENT);
  const anchorRef = useRef(NO_ELEMENT);
  const hasCategories = useSelector(getHasCategories);
  const totalNumberOfFaqs = useSelector(withCurrentPage(getNumberOfFaqs));
  const faqs = useSelector(getFaqs);
  const title = useSelector(getHeader);
  const isMobile = useSelector(isMobileLayout);
  const visibleFilter = useSelector(withCurrentPage(showFilter));
  const filterRef = useBrandContentScroll(brandContentDisplayType);
  const reduxDispatch = useDispatch();
  const isBrandPage = useSelector(hasBrandContent);
  const webCatalog = useSelector(withCurrentPage(selectWebCatalog));
  const brandContent = useSelector(withCurrentPage(getBrandContent));

  const createGtmProduct = (product: ProductlistViewProduct, index: number): GtmItemProduct => {
    const listName = isBrandPage
      ? getBrandListName(webCatalog, brandContent?.brandName)
      : getProductListName(webCatalog, product.url);
    return mapProductListViewProductToGtm({ product, indexInList: index, listName });
  };

  const gtmOnProductClick = (product: ProductlistViewProduct, index: number, pageNbr: number) => {
    const gtmItemProduct = createGtmProduct(product, index);

    reduxDispatch(GtmSelectItem({ gtmItemProduct, listPageNbr: pageNbr }));
  };

  const gtmOnViewItem = (items: GtmViewItem[], pageNbr: number) => {
    const gtmItemProducts = items.map(item => {
      return createGtmProduct(item.product, item.index);
    });

    reduxDispatch(GtmViewListItem({ gtmItemProducts, listPageNbr: pageNbr }));
  };

  return (
    <div>
      <Header />
      {hasCategories && (
        <Section contentClassName={styles.categoryNavContent} full={isMobile}>
          <CategoryNavigation />
        </Section>
      )}
      <a ref={anchorRef} />
      {visibleFilter && <Filter ref={filterRef} />}
      <ProductList gtmOnItemClick={gtmOnProductClick} gtmOnViewItem={gtmOnViewItem} />
      {totalNumberOfFaqs !== undefined && totalNumberOfFaqs > 0 && (
        <Section contentClassName={styles.categoryNavContent}>
          <CategoryFaqs
            faqs={faqs}
            totalNumberOfFaqs={totalNumberOfFaqs}
            title={`${rm('product_faq_header')} ${title}`}
            phoneSize="1"
            tabletSize="2"
          />
        </Section>
      )}
      <ProductlistPaging anchorRef={anchorRef} />
      <FilteredCategoryNavigation />
      <div ref={footerRef} />
      <BrandContentBottom />
    </div>
  );
};
